/**
 * In-memory MFA for the mock API client (tracker item 63). Same rules as the
 * backend where the UI depends on them: enrollment needs the password, the
 * confirm returns 10 recovery codes once and a fresh token, a login with MFA
 * on is a challenge (5 minutes, single use), recovery codes are single use,
 * five wrong codes lock for 15 minutes, disable / new codes need the
 * password and a code, admins reset other users only, the policy names staff
 * roles only.
 *
 * The mock cannot run an authenticator: it accepts MOCK_TOTP_CODE ("123456")
 * as the current code, and replay protection is not simulated. The
 * enrollment gate on other routes is not simulated either (the login answer
 * and the status carry the flag).
 */
import * as types from './types';

export const MOCK_TOTP_CODE = '123456';

const RECOVERY_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const QR_PLACEHOLDER =
  'data:image/svg+xml;base64,' +
  btoa('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10"><rect width="10" height="10" fill="#fff"/><rect x="1" y="1" width="3" height="3"/><rect x="6" y="1" width="3" height="3"/><rect x="1" y="6" width="3" height="3"/></svg>');

export class MockMfaError extends Error {
  constructor(public status: number, message: string, public code: string) {
    super(message);
  }
}

interface MockMfaUser {
  id: string;
  tenantId: string;
  email: string;
  role: string;
  password: string;
}

interface MfaState {
  enabled: boolean;
  enabledAt: string | null;
  pending: boolean;
  recoveryCodes: Map<string, boolean>; // normalized code -> used
  failures: number;
  lockedUntil: number | null;
}

interface Challenge {
  tenantId: string;
  userId: string;
  expiresAt: number;
  used: boolean;
  attempts: number;
}

const STAFF_ROLES = ['ADMIN', 'OFFICE_MANAGER', 'OFFICE', 'TECHNICIAN'];
const normalize = (code: string) => code.toUpperCase().replace(/[\s-]/g, '');

export class MockMfa {
  private states = new Map<string, MfaState>();
  private challenges = new Map<string, Challenge>();
  private policies = new Map<string, types.MfaEnforceableRole[]>();
  now: () => number = () => Date.now();

  private key(tenantId: string, userId: string) {
    return `${tenantId}:${userId}`;
  }

  private state(user: MockMfaUser): MfaState {
    const k = this.key(user.tenantId, user.id);
    let s = this.states.get(k);
    if (!s) {
      s = { enabled: false, enabledAt: null, pending: false, recoveryCodes: new Map(), failures: 0, lockedUntil: null };
      this.states.set(k, s);
    }
    return s;
  }

  private locked(s: MfaState) {
    return s.lockedUntil !== null && s.lockedUntil > this.now();
  }

  private assertStaff(user: MockMfaUser) {
    if (!STAFF_ROLES.includes(user.role)) throw new MockMfaError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
  }

  private checkPassword(user: MockMfaUser, password: string | undefined) {
    if (!password) throw new MockMfaError(400, 'Your current password is required', 'VALIDATION_ERROR');
    if (password !== user.password) throw new MockMfaError(400, 'Current password is incorrect', 'INVALID_CURRENT_PASSWORD');
  }

  private newRecoveryCodes(s: MfaState): string[] {
    const codes: string[] = [];
    while (codes.length < 10) {
      let raw = '';
      for (let i = 0; i < 10; i++) raw += RECOVERY_ALPHABET[Math.floor(Math.random() * RECOVERY_ALPHABET.length)];
      const code = `${raw.slice(0, 5)}-${raw.slice(5)}`;
      if (!codes.includes(code)) codes.push(code);
    }
    s.recoveryCodes = new Map(codes.map((c) => [normalize(c), false]));
    return codes;
  }

  /** Accept a factor or count a failure (throws). */
  private useFactor(user: MockMfaUser, s: MfaState, factor: types.MfaFactor, wrongStatus: 400 | 401) {
    if (this.locked(s)) throw new MockMfaError(429, 'Too many wrong codes. Try again in a few minutes.', 'MFA_LOCKED');
    const code = factor.code?.trim() ?? '';
    const recovery = factor.recoveryCode?.trim() ?? '';
    if ((code === '') === (recovery === '')) {
      throw new MockMfaError(400, 'Enter the 6-digit code from your authenticator app, or a recovery code', 'VALIDATION_ERROR');
    }
    let ok = false;
    if (code) {
      if (!/^\d{6}$/.test(code.replace(/\s/g, ''))) throw new MockMfaError(400, 'The code must be 6 digits', 'VALIDATION_ERROR');
      ok = code.replace(/\s/g, '') === MOCK_TOTP_CODE;
    } else {
      const n = normalize(recovery);
      if (s.recoveryCodes.get(n) === false) {
        s.recoveryCodes.set(n, true);
        ok = true;
      }
    }
    if (ok) {
      s.failures = 0;
      return;
    }
    s.failures += 1;
    if (s.failures >= 5) {
      s.failures = 0;
      s.lockedUntil = this.now() + 15 * 60_000;
      throw new MockMfaError(429, 'Too many wrong codes. Try again in a few minutes.', 'MFA_LOCKED');
    }
    throw new MockMfaError(wrongStatus, 'That code is not right', 'MFA_INVALID_CODE');
  }

  // ---- login

  /** After a correct password: a challenge token when MFA is on, else whether enrollment is required. */
  loginStep(user: MockMfaUser): { challenge: types.MfaLoginChallenge } | { enrollmentRequired: boolean } {
    if (!STAFF_ROLES.includes(user.role)) return { enrollmentRequired: false };
    const s = this.state(user);
    if (s.enabled) {
      if (this.locked(s)) throw new MockMfaError(429, 'Too many wrong codes. Try again in a few minutes.', 'MFA_LOCKED');
      const token = `mock-challenge-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
      const expiresAt = this.now() + 5 * 60_000;
      this.challenges.set(token, { tenantId: user.tenantId, userId: user.id, expiresAt, used: false, attempts: 0 });
      return { challenge: { mfaRequired: true, challengeToken: token, expiresAt: new Date(expiresAt).toISOString() } };
    }
    return { enrollmentRequired: this.isRequired(user) };
  }

  /** The user the challenge belongs to, once the factor is accepted. */
  verifyChallenge(token: string, factor: types.MfaFactor, findUser: (tenantId: string, userId: string) => MockMfaUser | undefined): MockMfaUser {
    const invalid = () => new MockMfaError(401, 'This sign-in request is no longer valid. Sign in again.', 'MFA_CHALLENGE_INVALID');
    const ch = this.challenges.get(token);
    if (!ch || ch.used || ch.expiresAt <= this.now() || ch.attempts >= 5) throw invalid();
    const user = findUser(ch.tenantId, ch.userId);
    if (!user || !this.state(user).enabled) throw invalid();
    try {
      this.useFactor(user, this.state(user), factor, 401);
    } catch (e) {
      if (e instanceof MockMfaError && e.code !== 'VALIDATION_ERROR') ch.attempts += 1;
      throw e;
    }
    ch.used = true;
    return user;
  }

  // ---- self-service

  isRequired(user: MockMfaUser): boolean {
    return (this.policies.get(user.tenantId) ?? []).includes(user.role as types.MfaEnforceableRole);
  }

  status(user: MockMfaUser): types.MfaStatus {
    this.assertStaff(user);
    const s = this.state(user);
    return {
      available: true,
      enabled: s.enabled,
      enabledAt: s.enabledAt,
      pendingEnrollment: !s.enabled && s.pending,
      required: this.isRequired(user),
      recoveryCodesRemaining: s.enabled ? [...s.recoveryCodes.values()].filter((used) => !used).length : 0,
      lockedUntil: this.locked(s) ? new Date(s.lockedUntil as number).toISOString() : null,
    };
  }

  start(user: MockMfaUser, password: string, issuer: string): types.MfaEnrollmentStart {
    this.assertStaff(user);
    this.checkPassword(user, password);
    const s = this.state(user);
    if (s.enabled) throw new MockMfaError(409, 'Two-step verification is already on', 'MFA_ALREADY_ENABLED');
    s.pending = true;
    const secret = 'JBSWY3DPEHPK3PXPJBSWY3DPEHPK3PXP';
    return {
      secret,
      otpauthUri: `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(user.email)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`,
      qrCodeDataUrl: QR_PLACEHOLDER,
      expiresAt: new Date(this.now() + 15 * 60_000).toISOString(),
      issuer,
      account: user.email,
    };
  }

  confirm(user: MockMfaUser, code: string): string[] {
    this.assertStaff(user);
    const s = this.state(user);
    if (s.enabled) throw new MockMfaError(409, 'Two-step verification is already on', 'MFA_ALREADY_ENABLED');
    if (!s.pending) throw new MockMfaError(409, 'Start the setup again: it was not started or has expired', 'MFA_ENROLLMENT_NOT_STARTED');
    if (!/^\d{6}$/.test((code ?? '').replace(/\s/g, ''))) throw new MockMfaError(400, 'The code must be 6 digits', 'VALIDATION_ERROR');
    if (code.replace(/\s/g, '') !== MOCK_TOTP_CODE) {
      throw new MockMfaError(400, 'That code is not right. Check the time on your phone and try the current code.', 'MFA_INVALID_CODE');
    }
    s.enabled = true;
    s.pending = false;
    s.enabledAt = new Date(this.now()).toISOString();
    s.failures = 0;
    s.lockedUntil = null;
    return this.newRecoveryCodes(s);
  }

  disable(user: MockMfaUser, reauth: types.MfaReauth): void {
    this.assertStaff(user);
    this.checkPassword(user, reauth.password);
    const s = this.state(user);
    if (!s.enabled) throw new MockMfaError(409, 'Two-step verification is not on', 'MFA_NOT_ENABLED');
    this.useFactor(user, s, reauth, 400);
    this.states.delete(this.key(user.tenantId, user.id));
  }

  regenerate(user: MockMfaUser, reauth: types.MfaReauth): string[] {
    this.assertStaff(user);
    this.checkPassword(user, reauth.password);
    const s = this.state(user);
    if (!s.enabled) throw new MockMfaError(409, 'Two-step verification is not on', 'MFA_NOT_ENABLED');
    this.useFactor(user, s, reauth, 400);
    return this.newRecoveryCodes(s);
  }

  // ---- admin

  private assertAdmin(user: MockMfaUser) {
    if (user.role !== 'ADMIN') throw new MockMfaError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
  }

  getPolicy(admin: MockMfaUser): types.MfaPolicy {
    this.assertAdmin(admin);
    return { requiredRoles: [...(this.policies.get(admin.tenantId) ?? [])], enforceableRoles: [...types.MFA_ENFORCEABLE_ROLES], available: true };
  }

  setPolicy(admin: MockMfaUser, roles: types.MfaEnforceableRole[]): types.MfaPolicy {
    this.assertAdmin(admin);
    if (!Array.isArray(roles) || roles.some((r) => !types.MFA_ENFORCEABLE_ROLES.includes(r))) {
      throw new MockMfaError(400, `MFA can only be required for ${types.MFA_ENFORCEABLE_ROLES.join(', ')}`, 'VALIDATION_ERROR');
    }
    this.policies.set(admin.tenantId, types.MFA_ENFORCEABLE_ROLES.filter((r) => roles.includes(r)));
    return this.getPolicy(admin);
  }

  listStatuses(admin: MockMfaUser): types.UserMfaStatus[] {
    this.assertAdmin(admin);
    const out: types.UserMfaStatus[] = [];
    this.states.forEach((s, k) => {
      const [tenantId, userId] = k.split(':');
      if (tenantId !== admin.tenantId) return;
      out.push({ userId, enabled: s.enabled, enabledAt: s.enabledAt, lockedUntil: this.locked(s) ? new Date(s.lockedUntil as number).toISOString() : null });
    });
    return out.sort((a, b) => a.userId.localeCompare(b.userId));
  }

  reset(admin: MockMfaUser, target: MockMfaUser | undefined, reauth: types.MfaReauth): { hadMfa: boolean } {
    this.assertAdmin(admin);
    if (target && target.id === admin.id) {
      throw new MockMfaError(400, 'Use "Turn off" on your own security page to change your own two-step verification', 'MFA_SELF_RESET');
    }
    this.checkPassword(admin, reauth.password);
    if (!target || target.tenantId !== admin.tenantId) throw new MockMfaError(404, 'User not found', 'NOT_FOUND');
    const adminState = this.state(admin);
    if (adminState.enabled) this.useFactor(admin, adminState, reauth, 400);
    const k = this.key(target.tenantId, target.id);
    const hadMfa = this.states.get(k)?.enabled ?? false;
    this.states.delete(k);
    return { hadMfa };
  }
}
